import { apiClient } from './api'

export type SupportTicketStatus =
  | 'open'
  | 'in_progress'
  | 'waiting_customer'
  | 'resolved'
  | 'closed'
export type SupportTicketPriority = 'low' | 'medium' | 'high' | 'critical'
export type SupportTicketCategory =
  | 'technical'
  | 'billing'
  | 'network'
  | 'security'
  | 'general'
  | 'other'
export type SupportMessageVisibility = 'customer' | 'internal'

export type SupportTicketAttachment = {
  id: string
  message_id: string
  file_name: string
  content_type: string
  size_bytes: number
  public_url: string
  created_at: string
}

export type SupportTicketMessage = {
  id: string
  ticket_id: string
  author_user_id: string | null
  author_org_id: string | null
  author_name: string | null
  author_org_name: string | null
  body: string
  visibility: SupportMessageVisibility
  is_system: boolean
  attachments: SupportTicketAttachment[]
  created_at: string
}

export type SupportTicketEvent = {
  id: string
  ticket_id: string
  event_type: string
  note: string | null
  event_data: Record<string, unknown>
  actor_user_id: string | null
  actor_org_id: string | null
  actor_name: string | null
  actor_org_name: string | null
  created_at: string
}

export type SupportTicketSummary = {
  id: string
  ticket_number: string
  subject: string
  description_preview: string
  category: SupportTicketCategory | string
  priority: SupportTicketPriority | string
  status: SupportTicketStatus | string

  origin_org_id: string
  reseller_org_id: string
  cloud_provider_org_id: string
  current_owner_org_id: string
  current_owner_level: 'reseller' | 'cloud_provider' | string
  escalation_level: number

  opened_by_user_id: string | null
  assigned_to_user_id: string | null
  vm_instance_id: string | null

  created_at: string
  updated_at: string
  last_message_at: string
  resolved_at: string | null
  closed_at: string | null
}

export type SupportTicketDetail = {
  description: string
  messages: SupportTicketMessage[]
  events: SupportTicketEvent[]
} & SupportTicketSummary

export type SupportTicketListResponse = {
  total: number
  tickets: SupportTicketSummary[]
}

export type SupportTicketListParams = {
  queue?: string
  status?: string
  priority?: string
  category?: string
  search?: string
  skip?: number
  limit?: number
}

function buildListQuery(params?: SupportTicketListParams): string {
  const qs = new URLSearchParams()

  if (!params) {
    return ''
  }

  if (params.queue) qs.set('queue', params.queue)
  if (params.status) qs.set('status', params.status)
  if (params.priority) qs.set('priority', params.priority)
  if (params.category) qs.set('category', params.category)
  if (params.search) qs.set('search', params.search)
  if (typeof params.skip === 'number') qs.set('skip', String(params.skip))
  if (typeof params.limit === 'number') qs.set('limit', String(params.limit))

  const query = qs.toString()
  return query ? `?${query}` : ''
}

function appendAttachments(formData: FormData, attachments?: File[]) {
  if (!attachments || attachments.length === 0) {
    return
  }

  attachments.forEach((file) => {
    formData.append('attachments', file)
  })
}

export type CreateSupportTicketInput = {
  subject: string
  description: string
  category: SupportTicketCategory
  priority: SupportTicketPriority
  reseller_id?: string
  vm_instance_id?: string
  attachments?: File[]
}

export type AddSupportMessageInput = {
  body: string
  visibility?: SupportMessageVisibility
  attachments?: File[]
}

export const supportApi = {
  ec: {
    listTickets: async (params?: SupportTicketListParams): Promise<SupportTicketListResponse> => {
      return await apiClient.get(`/api/v1/end-customer/support/tickets${buildListQuery(params)}`)
    },

    getTicket: async (ticketId: string): Promise<SupportTicketDetail> => {
      return await apiClient.get(`/api/v1/end-customer/support/tickets/${ticketId}`)
    },

    createTicket: async (input: CreateSupportTicketInput): Promise<SupportTicketDetail> => {
      const formData = new FormData()
      formData.append('subject', input.subject)
      formData.append('description', input.description)
      formData.append('category', input.category)
      formData.append('priority', input.priority)
      if (input.reseller_id) formData.append('reseller_id', input.reseller_id)
      if (input.vm_instance_id) formData.append('vm_instance_id', input.vm_instance_id)
      appendAttachments(formData, input.attachments)

      return await apiClient.postFormData('/api/v1/end-customer/support/tickets', formData)
    },

    addMessage: async (
      ticketId: string,
      input: AddSupportMessageInput,
    ): Promise<SupportTicketDetail> => {
      const formData = new FormData()
      formData.append('body', input.body || '')
      appendAttachments(formData, input.attachments)

      return await apiClient.postFormData(
        `/api/v1/end-customer/support/tickets/${ticketId}/messages`,
        formData,
      )
    },

    closeTicket: async (ticketId: string, note?: string): Promise<SupportTicketDetail> => {
      return await apiClient.post(`/api/v1/end-customer/support/tickets/${ticketId}/close`, {
        note: note || null,
      })
    },
  },

  rs: {
    listTickets: async (params?: SupportTicketListParams): Promise<SupportTicketListResponse> => {
      return await apiClient.get(`/api/v1/reseller/support/tickets${buildListQuery(params)}`)
    },

    getTicket: async (ticketId: string): Promise<SupportTicketDetail> => {
      return await apiClient.get(`/api/v1/reseller/support/tickets/${ticketId}`)
    },

    addMessage: async (
      ticketId: string,
      input: AddSupportMessageInput,
    ): Promise<SupportTicketDetail> => {
      const formData = new FormData()
      formData.append('body', input.body || '')
      formData.append('visibility', input.visibility || 'customer')
      appendAttachments(formData, input.attachments)

      return await apiClient.postFormData(
        `/api/v1/reseller/support/tickets/${ticketId}/messages`,
        formData,
      )
    },

    updateStatus: async (
      ticketId: string,
      status: SupportTicketStatus,
      note?: string,
    ): Promise<SupportTicketDetail> => {
      return await apiClient.post(`/api/v1/reseller/support/tickets/${ticketId}/status`, {
        status,
        note: note || null,
      })
    },

    assignTicket: async (
      ticketId: string,
      assigneeUserId?: string | null,
      note?: string,
    ): Promise<SupportTicketDetail> => {
      return await apiClient.post(`/api/v1/reseller/support/tickets/${ticketId}/assign`, {
        assignee_user_id: assigneeUserId || null,
        note: note || null,
      })
    },

    escalateTicket: async (ticketId: string, reason: string): Promise<SupportTicketDetail> => {
      return await apiClient.post(`/api/v1/reseller/support/tickets/${ticketId}/escalate`, {
        reason,
      })
    },
  },

  cp: {
    listTickets: async (params?: SupportTicketListParams): Promise<SupportTicketListResponse> => {
      return await apiClient.get(`/api/v1/cloud-provider/support/tickets${buildListQuery(params)}`)
    },

    getTicket: async (ticketId: string): Promise<SupportTicketDetail> => {
      return await apiClient.get(`/api/v1/cloud-provider/support/tickets/${ticketId}`)
    },

    addInternalMessage: async (
      ticketId: string,
      body: string,
      attachments?: File[],
    ): Promise<SupportTicketDetail> => {
      const formData = new FormData()
      formData.append('body', body || '')
      appendAttachments(formData, attachments)

      return await apiClient.postFormData(
        `/api/v1/cloud-provider/support/tickets/${ticketId}/messages`,
        formData,
      )
    },

    updateStatus: async (
      ticketId: string,
      status: SupportTicketStatus,
      note?: string,
    ): Promise<SupportTicketDetail> => {
      return await apiClient.post(`/api/v1/cloud-provider/support/tickets/${ticketId}/status`, {
        status,
        note: note || null,
      })
    },

    returnToReseller: async (ticketId: string, note?: string): Promise<SupportTicketDetail> => {
      return await apiClient.post(
        `/api/v1/cloud-provider/support/tickets/${ticketId}/return-to-reseller`,
        {
          note: note || null,
        },
      )
    },
  },
}
